import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

import CalendarThemes from './calendarThemes';
import Customize from './configuration/Customize';
import Footer from './Footer';
import Header from './Header';
import Preview from './Preview';
import { getPageLayoutFactory } from './pageTypes';
import ConfirmModal from './modals/ConfirmModal';
import ModalPortal from './modals/ModalPortal';

/* ---------- persistence ---------- */

const STORAGE_KEY = 'calendar.app.configuration';
const STORAGE_VERSION = 1;

function serializeConfiguration(config) {
	return {
		version: STORAGE_VERSION,
		payload: {
			...config,
			selectedDates: config.selectedDates.map(item => ({
				...item,
				startDate: item.startDate.toISOString(),
				endDate: item.endDate.toISOString()
			})),
			weeks: config.weeks.map(week => ({
				...week,
				date: week.date.toISO()
			}))
		}
	};
}

function deserializeConfiguration(raw) {
	if (!raw || raw.version !== STORAGE_VERSION) {
		return null;
	}

	const payload = raw.payload;

	return {
		...payload,
		selectedDates: payload.selectedDates.map(item => ({
			...item,
			startDate: new Date(item.startDate),
			endDate: new Date(item.endDate)
		})),
		weeks: payload.weeks.map(week => ({
			...week,
			date: DateTime.fromISO(week.date)
		}))
	};
}

/* ---------- transformation helpers ---------- */

function resolveRanges(dates) {
	const rangesResolved = dates.map(item => {
		if (item.startDate.toDateString() !== item.endDate.toDateString()) {
			const rangeItems = [];
			let currentDate = DateTime.fromJSDate(item.startDate);
			const endDate = DateTime.fromJSDate(item.endDate);

			while (currentDate <= endDate) {
				rangeItems.push({
					...item,
					date: currentDate.toJSDate()
				});
				currentDate = currentDate.plus({ day: 1 });
			}

			return rangeItems;
		}

		return [{ ...item, date: item.startDate }];
	});

	return rangesResolved.flat();
}

function deriveCalendarMarkings(selectedDates) {
	const resolved = resolveRanges(selectedDates);

	const grouped = resolved.reduce((acc, item) => {
		const key = DateTime.fromJSDate(item.date).toISODate();

		if (!acc[key]) {
			acc[key] = [];
		}
		acc[key].push(item);

		return acc;
	}, {});

	return Object.keys(grouped).map(date => ({
		date,
		cellDecorations: grouped[date].map(item => ({
			backgroundFillColor: item.colors.background,
			backgroundStrokeColor: item.colors.border,
			labelColor: item.colors.text,
			edgeInset: { left: 1, top: 1, right: 1, bottom: 1 }
		}))
	}));
}

/* ---------- defaults ---------- */

const defaultConfiguration = {
	pageTitleTemplate: '$YEAR$', // the old setting for older layouts, new layout should use the documentX objects (see below documentPageTitle)
	document: {
		marginsInMm: { left: 20, top: 10, right: 20, bottom: 10 },
		header: { visible: true, title: 'CalGen', titleSize: 10, titleAlignment: 'left'},
		title: { visible: true, title: '$YEAR$', titleSize: 20, titleAlignment: 'left'},
		pageTitle: { visible: true, title: '$YEAR$', titleSize: 20, titleAlignment: 'left'}, // the new property for the page level title (this title will be repeated on each <Page /> element added to the pdf by the layout, the same logic as pageTitleTemplate)
		sectionTitle: { visible: true, title: '$YEAR$', titleSize: 20, titleAlignment: 'left'}, // this is not the same as the pageTitle above, the layout defines what exactly a section is and if the layout has has these!
	},
	years: [{
	year: new Date().getFullYear(),
	months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
	}],
	weeks: [{
		year: DateTime.now().startOf('week').toFormat('kkkk'),
		week: DateTime.now().startOf('week').toFormat('W'),
		date: DateTime.now().startOf('week')
	}],
	prependZero: true,
	showWeekends: false,
	startWeekOnSundays: false,
	showWeekColumn: true,
	alignmentHeaderCell: 'center',
	alignmentContentCellHorizontal: 'right',
	alignmentContentCellVertical: 'bottom',
	alignmentWeekCell: 'top',
	themeNameOrTheme: 'default',
	orientation: 'portrait',
	layout: 'yearPerPage',
	layoutConfig: { 'flexible': {
		sectionTitleAlignment: 'center',
		firstRowMarginTopInMm: 2,
		marginBottomInMm: 5,
		lastRowInSectionMarginBottomInMm: 15,
	}},
	paperSize: 'A4',
	selectedDates: [{
		id: crypto.randomUUID(),
		showDatePicker: false,
		dateValid: false,
		startDate: DateTime.now().toJSDate(),
		endDate: DateTime.now().toJSDate(),
		colors: {
			background: null,
			border: null,
			text: '#000000'
		}
	}],
	startOfDay: 9,
	endOfDay: 17,
};

/* ---------- App ---------- */

function App() {
	const [configuration, setConfiguration] = useState(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			return stored
				? deserializeConfiguration(JSON.parse(stored)) ?? defaultConfiguration
				: defaultConfiguration;
		} catch {
			return defaultConfiguration;
		}
	});

	const [showResetConfirm, setShowResetConfirm] = useState(false);

	const requestReset = () => {
		setShowResetConfirm(true);
	};

	const cancelReset = () => {
		setShowResetConfirm(false);
	};

	const confirmReset = () => {
		localStorage.removeItem(STORAGE_KEY);
		setConfiguration(defaultConfiguration);
		setShowResetConfirm(false);
		window.location.reload(); // Force UI update
	};

	useEffect(() => {
		const serialized = serializeConfiguration(configuration);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
	}, [configuration]);

	const calendarMarkings = useMemo(
		() => deriveCalendarMarkings(configuration.selectedDates),
		[configuration.selectedDates]
	);

	const factoryConfig = useMemo(
		() => ({
			...configuration,
			customMarkings: calendarMarkings
		}),
		// configuration not added
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			configuration.layout,
			configuration.document,
			configuration?.layoutConfig[configuration.layout],
			configuration.orientation,
			configuration.paperSize,
			configuration.years,
			configuration.weeks,
			configuration.startOfDay,
			configuration.endOfDay,
			configuration.themeNameOrTheme,
			configuration.pageTitleTemplate,
			configuration.prependZero,
			configuration.showWeekends,
			configuration.startWeekOnSundays,
			configuration.showWeekColumn,
			configuration.alignmentHeaderCell,
			configuration.alignmentContentCellHorizontal,
			configuration.alignmentContentCellVertical,
			configuration.alignmentWeekCell,
			calendarMarkings
		]
	);

	return (
		<div className="h-full w-full flex flex-col bg-white">
			<div className="bg-yellow-600 shadow-lg">
				<Header onReset={requestReset} />
			</div>

			<div className="w-full flex-grow flex px-2">
				<div
					style={{
						width: '90%',
						height: 'calc(100vh - 72px - 41px - 8px)',
						overflowY: 'scroll'
					}}
					className="w-2/4 mr-2 flex flex-col"
				>
					<Customize
						themes={CalendarThemes()}
						configuration={configuration}
						update={setConfiguration}
					/>
				</div>

				<div className="w-full flex-grow overflow-hidden">
					<Preview
						pageFactory={() =>
							getPageLayoutFactory(
								factoryConfig.layout,
								factoryConfig.orientation,
								factoryConfig.paperSize
							)(factoryConfig)
						}
						configuration={factoryConfig}
					/>
				</div>
			</div>

			<div className="bg-gray-50 mt-2">
				<Footer />
			</div>

			{showResetConfirm && (<ModalPortal>
				<ConfirmModal
					title="Reset configuration"
					message="This will reset all settings to their default values. This action cannot be undone."
					onConfirm={confirmReset}
					onCancel={cancelReset}
				/>

			</ModalPortal>)}
		</div>


	);
}

export default App;